import React, { useEffect, useState } from "react";
import { X, Calendar, User as UserIcon, Clock, ArrowRight } from "lucide-react";
import axiosInstance from "../../utils/axiosInstance";
import toast from "react-hot-toast";

export default function ExtensionHistoryModal({ isOpen, onClose, userId, userName }) {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && userId) {
      fetchExtensionHistory();
    }
  }, [isOpen, userId]);

  const fetchExtensionHistory = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(
        `/subscription/${userId}/extension-history`
      );
      setHistory(response.data.responseData);
    } catch (error) {
      toast.error("Failed to load extension history");
      console.error("Extension history error:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Extension History
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {userName}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500 mb-4"></div>
              <p className="text-gray-500 dark:text-gray-400">Loading history...</p>
            </div>
          ) : !history || history.extensions?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Clock className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" />
              <p className="text-gray-500 dark:text-gray-400 text-center">
                No extension history available
              </p>
              <p className="text-sm text-gray-400 dark:text-gray-500 text-center mt-2 max-w-md">
                Extension history is only available for extensions made after the audit system was deployed. 
                Previous extensions are not tracked in the system.
              </p>
            </div>
          ) : (
            <>
              {/* Extension List */}
              <div className="space-y-4 mb-6">
                {history.extensions.map((ext, index) => (
                  <div
                    key={ext.auditId || index}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-orange-300 dark:hover:border-orange-600 transition-colors"
                  >
                    {/* Extension Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {formatDateTime(ext.extensionDate)}
                          </span>
                        </div>
                      </div>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          ext.daysAdded > 0
                            ? "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400"
                            : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                        }`}
                      >
                        {ext.daysAdded > 0 ? "+" : ""}
                        {ext.daysAdded} days
                      </span>
                    </div>

                    {/* Admin Info */}
                    <div className="flex items-center gap-2 mb-3">
                      <UserIcon className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        Admin: {ext.adminName || "System"}
                      </span>
                    </div>

                    {/* Date Change */}
                    <div className="flex items-center gap-2 text-xs bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
                      <div className="flex-1">
                        <div className="text-gray-500 dark:text-gray-400 mb-1">
                          Previous End Date
                        </div>
                        <div className="font-medium text-gray-700 dark:text-gray-300">
                          {formatDate(ext.oldEndDate)}
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-orange-500 flex-shrink-0" />
                      <div className="flex-1">
                        <div className="text-gray-500 dark:text-gray-400 mb-1">
                          New End Date
                        </div>
                        <div className="font-medium text-orange-600 dark:text-orange-400">
                          {formatDate(ext.newEndDate)}
                        </div>
                      </div>
                    </div>

                    {/* Reason (if provided) */}
                    {ext.reason && (
                      <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                          Reason
                        </div>
                        <div className="text-sm text-gray-700 dark:text-gray-300">
                          {ext.reason}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Summary */}
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-900/10 rounded-lg p-4 border border-orange-200 dark:border-orange-800">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                      Total Extensions
                    </div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                      {history.totalExtensions}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                      Total Days Added
                    </div>
                    <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                      {history.totalDaysAdded > 0 ? "+" : ""}
                      {history.totalDaysAdded}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-800/50">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
